import { getCurrentAuthToken } from "./authToken";

const CART_TOKEN_KEY = "cartToken";
const CART_TOKEN_PREFIX = "cartToken:";
const CART_COOKIE_NAME = "ammachekku_cart_token";
const CART_EVENT_NAME = "ammachekku-cart-token-changed";
const CART_COOKIE_LIFETIME = 60 * 60 * 24 * 180;

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

const getCookieDomain = () => {
  if (!isBrowser) {
    return "";
  }

  const host = window.location.hostname.toLowerCase().replace(/^www\./, "");

  if (!host || host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return "";
  }

  const parts = host.split(".");

  if (parts.length < 2) {
    return host;
  }

  return `.${parts.slice(-2).join(".")}`;
};

const readCookie = (name) => {
  if (!isBrowser) {
    return "";
  }

  const cookiePrefix = `${name}=`;
  const matchedCookie = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(cookiePrefix));

  if (!matchedCookie) {
    return "";
  }

  return decodeURIComponent(matchedCookie.slice(cookiePrefix.length));
};

const writeCookie = (token) => {
  if (!isBrowser) {
    return;
  }

  const domain = getCookieDomain();
  const baseSegments = [`${CART_COOKIE_NAME}=${encodeURIComponent(token || "")}`, "path=/", "SameSite=Lax"];

  if (domain) {
    baseSegments.push(`domain=${domain}`);
  }

  if (window.location.protocol === "https:") {
    baseSegments.push("Secure");
  }

  if (token) {
    baseSegments.push(`max-age=${CART_COOKIE_LIFETIME}`);
  } else {
    baseSegments.push("expires=Thu, 01 Jan 1970 00:00:00 GMT", "max-age=0");
  }

  document.cookie = baseSegments.join("; ");

  if (!token) {
    const segmentsWithoutDomain = baseSegments.filter((segment) => !segment.startsWith("domain="));
    document.cookie = segmentsWithoutDomain.join("; ");
  }
};

const dispatchCartTokenChanged = (token) => {
  if (!isBrowser) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CART_EVENT_NAME, {
      detail: { token: token || "" },
    })
  );
};

const generateCartToken = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getAuthToken = () => {
  if (!isBrowser) {
    return "";
  }

  return getCurrentAuthToken() || "";
};

const getAuthIdentity = () => getAuthToken() || "guest";

const getCartStorageKey = (identity = getAuthIdentity()) => {
  const normalizedIdentity = String(identity || "guest");
  return normalizedIdentity === "guest"
    ? CART_TOKEN_KEY
    : `${CART_TOKEN_PREFIX}${normalizedIdentity}`;
};

const persistInStorage = (token, identity = getAuthIdentity()) => {
  if (!isBrowser || !token) {
    return;
  }

  const storageKey = getCartStorageKey(identity);
  localStorage.setItem(storageKey, token);

  if (storageKey !== CART_TOKEN_KEY) {
    localStorage.setItem(CART_TOKEN_KEY, token);
  }
};

const resolveStoredToken = (identity = getAuthIdentity()) => {
  if (!isBrowser) {
    return "";
  }

  const storageKey = getCartStorageKey(identity);
  const existing = localStorage.getItem(storageKey);
  if (existing) {
    return existing;
  }

  if (storageKey !== CART_TOKEN_KEY) {
    return localStorage.getItem(CART_TOKEN_KEY) || "";
  }

  return "";
};

export const storeCartToken = (token, identity = getAuthIdentity()) => {
  const normalizedToken = String(token || "").trim();

  if (!isBrowser || !normalizedToken) {
    return "";
  }

  persistInStorage(normalizedToken, identity);
  writeCookie(normalizedToken);
  dispatchCartTokenChanged(normalizedToken);
  return normalizedToken;
};

export const clearCartToken = () => {
  if (!isBrowser) {
    return;
  }

  const authIdentity = getAuthIdentity();
  localStorage.removeItem(CART_TOKEN_KEY);
  localStorage.removeItem(getCartStorageKey(authIdentity));
  writeCookie("");
  dispatchCartTokenChanged("");
};

export const getSharedCartToken = () => readCookie(CART_COOKIE_NAME);

export const getCartToken = (identity = getAuthIdentity()) => {
  const cookieToken = getSharedCartToken();
  if (cookieToken) {
    persistInStorage(cookieToken, identity);
    return cookieToken;
  }

  const storedToken = resolveStoredToken(identity);
  if (storedToken) {
    writeCookie(storedToken);
    return storedToken;
  }

  return storeCartToken(generateCartToken(), identity);
};

export const CART_TOKEN_EVENT = CART_EVENT_NAME;